import { createContext, JSX, ReactNode, useContext } from 'react';
import * as vp from 'vopay';
import { usePermissions } from './Permissions.provider';
import { useClientData } from './ClientData.provider';
import { useApprovalWorkflow } from './ApprovalWorkflow.provider';

export type SupportedModules =
  | 'dashboard'
  | 'myAccount'
  | 'transactionApproval'
  | 'subAccounts'
  | 'clientAccounts'
  | 'eLinx'
  | 'payment'
  | 'collection'
  | 'conversion'
  | 'contacts'
  | 'upload'
  | 'software'
  | 'transactions'
  | 'statements'
  | 'integrations'
  | 'settings'
  | 'support'
  | 'users'
  | 'globalSend'
  | 'disputeManagement';

export type FeatureFlagCtx = {
  modules?: {
    dashboard?: {
      isEnabled: boolean;
    };
    myAccount?: {
      isEnabled: boolean;
      AccountSummary: boolean;
      FundMyAccount: boolean | undefined;
      WithdrawMyAccount: boolean | undefined;
      OperationalAccounts: boolean | undefined;
      BankAccountTransfer: boolean | undefined;
      ViewAccountAndBilling: boolean | undefined;
      AutoBalanceTransfer: boolean | undefined;
    };
    subAccounts?: {
      isEnabled: boolean;
      ViewSubaccounts: boolean | undefined;
      CreateAccountTransfer: boolean | undefined;
    };
    transactionApproval?: {
      isEnabled: boolean;
      Configuration: boolean;
      Inbox: boolean;
    };
    clientAccounts?: {
      isEnabled: boolean;
      ViewClientAccounts: boolean | undefined;
      CreateClientAccountTransfers: boolean | undefined;
      AddClientAccount: boolean | undefined;
    };
    eLinx?: {
      isEnabled: boolean;
      ViewPaylinkHitory: boolean | undefined;
      CreateElinxConnect: boolean | undefined;
      CreateElinxPay: boolean | undefined;
      CreateElinxCollect: boolean | undefined;
      CreateElinxFlex: boolean | undefined;
    };
    payment?: {
      isEnabled: boolean;
      CreatePayments: boolean | undefined;
      BulkPayments: boolean | undefined;
      BillPayment: boolean | undefined;
    };
    collection?: {
      isEnabled: boolean;
      CreateCollections: boolean | undefined;
      BulkCollections: boolean | undefined;
    };
    conversion?: {
      isEnabled: boolean;
      CreateConversions: boolean | undefined;
    };
    contacts?: {
      isEnabled: boolean;
      ViewBeneficiaries: boolean | undefined;
      AddBeneficiaries: boolean | undefined;
    };
    upload?: {
      isEnabled: boolean;
      CreateBulkFileUpload: boolean | undefined;
      ViewBulkFileUpload: boolean | undefined;
    };
    software?: {
      isEnabled: boolean;
      ViewAccountingBillsAndInvoices: boolean | undefined;
    };
    transactions?: {
      isEnabled: boolean;
      ViewScheduledTransactions: boolean | undefined;
    };
    statements?: {
      isEnabled: boolean;
      ViewReconciliation: boolean | undefined;
      ViewTransactionHistory: boolean | undefined;
      ViewBankVerify: boolean | undefined;
      ViewTransactionReport: boolean | undefined;
      CreateTransactionReport: boolean | undefined;
    };
    integrations?: {
      isEnabled: boolean;
      ViewSettings: boolean | undefined;
    };
    settings?: {
      isEnabled: boolean;
    };
    support?: {
      isEnabled: boolean;
      ViewSupportTickets: boolean | undefined;
      CreateSupportTickets: boolean | undefined;
      ViewLearningCentre: boolean | undefined;
    };
    users?: {
      isEnabled: boolean;
    };
    interestAccounts?: {
      isEnabled: boolean;
    };
    globalSend?: {
      isEnabled: boolean;
    };
    disputeManagement?: {
      isEnabled: boolean;
    };
  };
};

export const FeatureFlagContext = createContext<FeatureFlagCtx>({});

export const FeatureFlagProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { permissions } = usePermissions();
  const { response } = useClientData();
  const { isApprover } = useApprovalWorkflow();

  const hasAnyTrue = (obj: Record<string, any>) =>
    Object.entries(obj)
      .filter(([key]) => key !== 'isEnabled')
      .some(([, value]) => !!value);

  const myAccount = {
    AccountSummary: Boolean(permissions.ViewAccountSummary),
    FundMyAccount: permissions.CreateFundMyAccountTransactions,
    WithdrawMyAccount: permissions.CreateWithdrawMyAccountTransactions,
    BankAccountTransfer: permissions.CreateBankAccountTransfer,
    ViewAccountAndBilling: permissions.ViewAccountandBilling && !!response.AccountAndBillingEnabled,
    OperationalAccounts: permissions.ViewOperationalAccounts && !vp.toBool(response.ClientAccountsEnabled),
    AutoBalanceTransfer: permissions.CreateAutoBalanceTransfer,
  };
  const eLinxPermissions = {
    CreateElinxConnect: permissions.CreateElinxConnect,
    CreateElinxPay: permissions.CreateElinxPay && response.PaymentsEnabled,
    CreateElinxCollect: permissions.CreateElinxCollect && response.CollectionsEnabled,
    CreateElinxFlex: permissions.CreateElinxFlex && response.CollectionsEnabled,
  };
  const eLinx = {
    ViewPaylinkHitory: vp.toBool(response.PayLinkEnabled) && hasAnyTrue(eLinxPermissions),
    ...eLinxPermissions,
  };
  const values: FeatureFlagCtx = {
    modules: {
      dashboard: {
        isEnabled: true,
      },
      myAccount: {
        isEnabled: hasAnyTrue(myAccount),
        ...myAccount,
      },
      transactionApproval: {
        isEnabled:
          ((isApprover && !!permissions.ApproveTransactions) || !!permissions.ViewTransactionApprovalWorkflow) &&
          response.SubAccountType !== 'franchise',
        Configuration: !!permissions.ViewTransactionApprovalWorkflow,
        Inbox: isApprover && !!permissions.ApproveTransactions,
      },
      subAccounts: {
        isEnabled: Boolean(permissions.ViewSubaccounts),
        ViewSubaccounts: permissions.ViewSubaccounts,
        CreateAccountTransfer:
          permissions.CreateAccountTransfer &&
          vp.toBool(response.AccountTransferEnabled) &&
          !vp.toBool(response.ClientAccountsEnabled),
      },
      clientAccounts: {
        isEnabled:
          Boolean(permissions.ViewClientAccounts || permissions.CreateClientAccountTransfers) &&
          vp.toBool(response.ClientAccountsEnabled),
        ViewClientAccounts: permissions.ViewClientAccounts,
        CreateClientAccountTransfers: permissions.CreateClientAccountTransfers,
        AddClientAccount: permissions.AddClientAccounts,
      },
      eLinx: {
        isEnabled: hasAnyTrue(eLinx) && vp.toBool(response.PayLinkEnabled) && response.SubAccountType !== 'franchise',
        ...eLinx,
      },
      payment: {
        isEnabled: Boolean(permissions.CreatePayments),
        CreatePayments: permissions.CreatePayments,
        BulkPayments: Boolean(permissions.ManageTransactionGroups),
        BillPayment: permissions.CreatePayments && response.Country === 'CA',
      },
      collection: {
        isEnabled: Boolean(permissions.CreateCollections) && response.SubAccountType !== 'franchise',
        CreateCollections: permissions.CreateCollections,
        BulkCollections: Boolean(permissions.ManageTransactionGroups),
      },
      conversion: {
        isEnabled: Boolean(permissions.CreateConversions),
        CreateConversions: permissions.CreateConversions,
      },
      contacts: {
        isEnabled:
          Boolean(permissions.ViewBeneficiaries) &&
          !vp.toBool(response.ClientAccountsEnabled) &&
          response.SubAccountType !== 'franchise',
        ViewBeneficiaries: permissions.ViewBeneficiaries,
        AddBeneficiaries: permissions.AddBeneficiaries,
      },
      upload: {
        isEnabled: Boolean(permissions.CreateBulkFileUpload || permissions.ViewBulkFileUpload),
        CreateBulkFileUpload: permissions.CreateBulkFileUpload,
        ViewBulkFileUpload: permissions.ViewBulkFileUpload,
      },
      software: {
        isEnabled:
          Boolean(permissions.ViewAccountingBillsAndInvoices) && Boolean(response.AccountingSoftwareIntegrationEnabled),
        ViewAccountingBillsAndInvoices: permissions.ViewAccountingBillsAndInvoices,
      },
      transactions: {
        isEnabled: Boolean(permissions.ViewScheduledTransactions),
        ViewScheduledTransactions: permissions.ViewScheduledTransactions,
      },
      statements: {
        isEnabled: Boolean(
          permissions.ViewTransactionHistory ||
            permissions.ViewTransactionReport ||
            permissions.ViewScheduledTransactions,
        ),
        ViewReconciliation: false, // disabled for now
        ViewTransactionHistory: permissions.ViewTransactionHistory,
        ViewBankVerify:
          response.MicroDepositBankAccountVerificationEnabled ||
          response.InteracBasicVerificationEnabled ||
          response.InteracEnhancedVerificationEnabled ||
          response.CardBasicVerificationEnabled ||
          response.CardEnhancedVerificationEnabled,
        ViewTransactionReport: permissions.ViewTransactionReport,
        CreateTransactionReport: permissions.CreateTransactionReport,
      },
      integrations: {
        isEnabled: Boolean(permissions.ViewSettings),
        ViewSettings: permissions.ViewSettings,
      },
      settings: {
        isEnabled: Boolean(permissions.ViewSettings),
      },
      support: {
        isEnabled: response.AccountID !== 'agmiinsurance',
        ViewSupportTickets: permissions.ViewSupportTickets,
        CreateSupportTickets: permissions.CreateSupportTickets,
        ViewLearningCentre: permissions.ViewLearningCentre,
      },
      interestAccounts: {
        isEnabled: Boolean(permissions.ViewInterestAccounts || permissions.ManageInterestAccounts),
      },
      globalSend: {
        isEnabled: vp.toBool(response.GCMEnabled) && Boolean(permissions.CreateConversions),
      },
      disputeManagement: {
        isEnabled: vp.toBool(response.CreditCardEnabled),
      },
    },
  };
  return <FeatureFlagContext.Provider value={values}>{children}</FeatureFlagContext.Provider>;
};

const useFeatureFlags = (): FeatureFlagCtx => useContext(FeatureFlagContext);

export const useModuleFeatureFlag = (module: SupportedModules): { [feature: string]: boolean } => {
  const featureFlags = useFeatureFlags();
  return featureFlags.modules?.[module] || {};
};
